<template>
  <div class="app-filter-date-range-field filter-field app-filter-field app-filter-field--range">
    <span v-if="label">{{ label }}</span>

    <VueDatePicker
      v-show="showInput"
      v-model="internalValue"
      range
      auto-apply
      :enable-time-picker="false"
      :locale="locale"
      :format="format"
      :placeholder="placeholder"
      :hide-input-icon="hideInputIcon"
      class="app-filter-date-range-picker"
      @update:model-value="onPickerUpdate"
    />

    <div class="app-filter-range-presets">
      <button
        v-for="preset in presets"
        :key="preset.key"
        type="button"
        class="app-filter-range-preset"
        :class="{ active: activePreset === preset.key }"
        @click="applyPreset(preset.key)"
      >
        {{ preset.label }}
      </button>

      <button
        v-if="showClearOption"
        type="button"
        class="app-filter-range-preset app-filter-range-preset--ghost"
        :class="{ active: isClearSelected }"
        @click="clearRange"
      >
        {{ clearLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

const DEFAULT_PRESETS = [
  { key: 'this_month', label: 'Este mes' },
  { key: 'last_month', label: 'Mes anterior' },
  { key: 'this_year', label: 'Año actual' },
  { key: 'last_year', label: 'Año anterior' },
];

export default {
  name: 'AppFilterDateRange',
  components: {
    VueDatePicker,
  },
  props: {
    modelValue: {
      type: Array,
      default: null,
    },
    label: {
      type: String,
      default: 'Fecha de negocio',
    },
    presets: {
      type: Array,
      default: () => DEFAULT_PRESETS,
    },
    initialPresetKey: {
      type: String,
      default: 'this_year',
    },
    locale: {
      type: String,
      default: 'es-MX',
    },
    format: {
      type: String,
      default: 'dd/MM/yyyy',
    },
    placeholder: {
      type: String,
      default: 'Selecciona rango de fecha',
    },
    hideInputIcon: {
      type: Boolean,
      default: true,
    },
    showInput: {
      type: Boolean,
      default: true,
    },
    clearLabel: {
      type: String,
      default: 'Sin filtro',
    },
    showClearOption: {
      type: Boolean,
      default: true,
    },
    clearPresetOnManualSelect: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      internalValue: null,
      activePreset: '',
      suppressNextPickerEvent: false,
      keepManualPresetClear: false,
      suppressedRangeValue: null,
    };
  },
  computed: {
    isClearSelected() {
      const range = Array.isArray(this.internalValue) ? this.internalValue : [];
      const start = this.normalizeDate(range[0]);
      const end = this.normalizeDate(range[1]);

      return !start && !end;
    },
  },
  mounted() {
    const hasExternalValue = Array.isArray(this.modelValue) && this.modelValue.length > 0;

    if (hasExternalValue) {
      this.internalValue = this.modelValue;
      this.activePreset = this.detectPresetFromRange(this.modelValue);
      return;
    }

    if (this.initialPresetKey !== '') {
      this.applyPreset(this.initialPresetKey);
    }
  },
  watch: {
    modelValue(value) {
      const normalized = Array.isArray(value) ? value : null;
      this.internalValue = normalized;

      if (this.clearPresetOnManualSelect && this.keepManualPresetClear) {
        this.activePreset = '';
        this.keepManualPresetClear = false;
        return;
      }

      this.activePreset = this.detectPresetFromRange(normalized);
    },
  },
  methods: {
    normalizeDate(value) {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return null;
      }

      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    },
    sameDate(left, right) {
      if (!(left instanceof Date) || !(right instanceof Date)) {
        return false;
      }

      return left.getTime() === right.getTime();
    },
    sameRange(leftRange, rightRange) {
      const left = Array.isArray(leftRange) ? leftRange : [];
      const right = Array.isArray(rightRange) ? rightRange : [];

      const leftStart = this.normalizeDate(left[0]);
      const leftEnd = this.normalizeDate(left[1]);
      const rightStart = this.normalizeDate(right[0]);
      const rightEnd = this.normalizeDate(right[1]);

      const startMatches = (leftStart === null && rightStart === null)
        || this.sameDate(leftStart, rightStart);
      const endMatches = (leftEnd === null && rightEnd === null)
        || this.sameDate(leftEnd, rightEnd);

      return startMatches && endMatches;
    },
    buildPresetRange(key) {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();

      if (key === 'this_month') {
        return [new Date(year, month, 1), new Date(year, month + 1, 0)];
      }

      if (key === 'last_month') {
        return [new Date(year, month - 1, 1), new Date(year, month, 0)];
      }

      if (key === 'this_year') {
        return [new Date(year, 0, 1), new Date(year, 11, 31)];
      }

      if (key === 'to_date') {
        return [new Date(year, 0, 1), new Date(year, month, now.getDate())];
      }

      if (key === 'last_year') {
        return [new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)];
      }

      return null;
    },
    detectPresetFromRange(rangeValue) {
      const range = Array.isArray(rangeValue) ? rangeValue : [];
      const start = this.normalizeDate(range[0]);
      const end = this.normalizeDate(range[1]);

      if (!start || !end) {
        return '';
      }

      for (const preset of this.presets) {
        const presetRange = this.buildPresetRange(preset.key);
        if (!Array.isArray(presetRange)) {
          continue;
        }

        const presetStart = this.normalizeDate(presetRange[0]);
        const presetEnd = this.normalizeDate(presetRange[1]);

        if (this.sameDate(start, presetStart) && this.sameDate(end, presetEnd)) {
          return preset.key;
        }
      }

      return '';
    },
    emitRangeChange(source, value, presetKey = '') {
      const range = Array.isArray(value) ? value : [];
      const start = this.normalizeDate(range[0]);
      const end = this.normalizeDate(range[1]);

      this.$emit('update:modelValue', value);
      this.$emit('change', {
        source,
        value,
        presetKey,
        isComplete: Boolean(start && end),
      });
    },
    onPickerUpdate(value) {
      if (this.suppressNextPickerEvent) {
        const shouldIgnoreAsProgrammaticEcho = this.sameRange(value, this.suppressedRangeValue);
        this.suppressNextPickerEvent = false;
        this.suppressedRangeValue = null;
        if (shouldIgnoreAsProgrammaticEcho) {
          return;
        }
      }

      this.internalValue = Array.isArray(value) ? value : null;

      if (this.clearPresetOnManualSelect) {
        this.activePreset = '';
        this.keepManualPresetClear = true;
      } else {
        this.activePreset = this.detectPresetFromRange(this.internalValue);
      }

      this.emitRangeChange('manual', this.internalValue);
    },
    applyPreset(presetKey) {
      const presetRange = this.buildPresetRange(presetKey);
      if (!Array.isArray(presetRange)) {
        return;
      }

      this.suppressNextPickerEvent = true;
      this.suppressedRangeValue = presetRange;
      this.keepManualPresetClear = false;
      this.internalValue = presetRange;
      this.activePreset = presetKey;
      this.emitRangeChange('preset', presetRange, presetKey);
    },
    clearRange() {
      this.suppressNextPickerEvent = true;
      this.suppressedRangeValue = null;
      this.keepManualPresetClear = false;
      this.internalValue = null;
      this.activePreset = '';
      this.emitRangeChange('clear', null);
    },
  },
};
</script>
